import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Chip,
  CircularProgress,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import {
  fetchMissionById,
  fetchMyWorkshopStatus,
  fetchMissionPayloads,
  fetchMyUplinkStatus,
  submitMissionPlan,
  fetchGroupLaunchChoice,
} from "../api/queries";
import { getSession } from "../api/session";
import { getMissionIcon } from "./missions/missionIcons";
import PayloadShowcase from "./missions/components/PayloadShowcase";
import TradeOffTable from "./missions/components/TradeOffTable";
import RiskMatrix from "./missions/components/RiskMatrix";
import MissionSpecPanel from "./missions/components/MissionSpecPanel";
import LaunchChoicePanel from "./missions/components/LaunchChoicePanel";
import { SheetAuthorsProvider } from "./missions/sheetAuthors";
import type { MissionRow, PayloadCategory, PayloadCategoryRow, PayloadOptionRow } from "../types/payload";

function mapCategories(
  categoryRows: PayloadCategoryRow[],
  optionRows: PayloadOptionRow[]
): PayloadCategory[] {
  return categoryRows.map((c) => ({
    id: c.id,
    label: c.label,
    customCriterionLabel: c.custom_criterion_label,
    options: optionRows
      .filter((o) => o.category_id === c.id)
      .map((o) => ({
        id: o.id,
        name: o.name,
        manufacturer: o.manufacturer,
        summary: o.summary ?? "",
        imageUrl: o.image_url ?? undefined,
        specSheetUrl: o.spec_sheet_url ?? undefined,
        ratings: {
          resolution: o.rating_resolution,
          energy: o.rating_energy,
          mass: o.rating_mass,
          cost: o.rating_cost,
          trl: o.rating_trl,
        },
      })),
  }));
}

// Renders any mission from Supabase: objective, needed payload list, and
// one payload showcase + trade-off table per category. Adding a mission
// means inserting rows (see supabase/sql/007_missions_payloads.sql), not
// writing a new page. What gets filled in on the sheet (specifications,
// trade-offs, risks) belongs to the participant's group and is shared live
// with the other members -- so the mission shown is always the group's one.
export default function MissionView(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const [mission, setMission] = React.useState<MissionRow | null>(null);
  const [group, setGroup] = React.useState<{ id: string; name: string } | null>(null);
  const [categories, setCategories] = React.useState<PayloadCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;
    const session = getSession();

    if (!session) {
      handlePageChange("Entry");
      return;
    }

    (async () => {
      const { data: status, error: statusError } = await fetchMyWorkshopStatus(session.email);
      if (!mounted) return;

      if (statusError) {
        setLoading(false);
        setError(statusError.message);
        return;
      }

      // No sheet until the participant has unlocked their group: back to the
      // mission chooser, or to the group name + code screen if a mission is
      // already chosen.
      if (!status?.chosen_mission_id || !status.group_id || !status.access_verified) {
        handlePageChange(status?.chosen_mission_id ? "WorkshopWaiting" : "Dashboard");
        return;
      }

      // A submitted plan is locked: the group is past the sheet and on its
      // way to (or already at) the command uplink.
      const { data: uplink } = await fetchMyUplinkStatus();
      if (!mounted) return;
      if (uplink?.plan_submitted) {
        handlePageChange(uplink.uplink_unlocked ? "CommandExecutor" : "UplinkWaiting");
        return;
      }

      setGroup({ id: status.group_id, name: status.group_name ?? "" });

      const { data: missionRow, error: missionError } = await fetchMissionById(status.chosen_mission_id);
      if (!mounted) return;

      if (missionError || !missionRow) {
        setLoading(false);
        setError(missionError?.message ?? null);
        return;
      }

      setMission(missionRow);

      const { categories: categoryRows, options: optionRows, error: payloadsError } = await fetchMissionPayloads(missionRow.id);

      if (!mounted) return;
      setLoading(false);

      if (payloadsError) {
        setError(payloadsError.message);
        return;
      }

      setCategories(mapCategories(categoryRows ?? [], optionRows ?? []));
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Another member may submit the plan while this one is still on the
  // sheet. Check now and then, so nobody keeps typing into a locked sheet.
  React.useEffect(() => {
    if (!group) return;
    const timer = window.setInterval(async () => {
      const { data: uplink } = await fetchMyUplinkStatus();
      if (uplink?.plan_submitted) handlePageChange("UplinkWaiting");
    }, 15000);
    return () => window.clearInterval(timer);
  }, [group, handlePageChange]);

  const confirmSubmit = async () => {
    if (!group) return;
    setSubmitting(true);
    setSubmitError(null);

    // The launch is part of the plan: without it, there'd be nothing to
    // launch after the hardware design.
    const { data: launchChoice } = await fetchGroupLaunchChoice(group.id);
    if (!launchChoice?.launch_option_id) {
      setSubmitting(false);
      setSubmitError("Choose how your satellite gets to space in the Launch section first.");
      return;
    }

    const { data: ok, error: submitErr } = await submitMissionPlan();

    setSubmitting(false);
    if (submitErr || !ok) {
      setSubmitError(submitErr?.message ?? "The mission plan couldn't be submitted. Please try again.");
      return;
    }

    setConfirmOpen(false);
    handlePageChange("UplinkWaiting");
  };

  const MissionIcon = mission ? getMissionIcon(mission.icon_key) : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: "#08050b",
        // The mission's own cover picture once it is loaded (the default
        // backdrop until then, or if the mission has none), under the same
        // dark wash so the sheet stays readable. The picture is pinned to the
        // window: the sheet is many screens tall, and stretching a photo
        // over all of it would blow it up beyond recognition.
        backgroundImage: `linear-gradient(180deg, rgba(9,5,11,0.95) 0%, rgba(9,5,11,0.86) 30%, rgba(46,16,84,0.55) 100%), url("${
          mission?.image_url || "/background.png"
        }")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: mission?.image_url ? "fixed" : "scroll",
        px: { xs: 2.5, sm: 4, md: 6 },
        pt: { xs: 9, sm: 9, md: 7 },
        pb: { xs: 4, sm: 5, md: 7 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 15% 10%, rgba(168,85,247,0.2), transparent 40%), radial-gradient(circle at 85% 85%, rgba(56,189,248,0.14), transparent 42%)",
          pointerEvents: "none",
        }}
      />

      <Stack spacing={{ xs: 3.5, md: 4.5 }} sx={{ width: "100%", maxWidth: 1120, position: "relative", zIndex: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={() => handlePageChange("Dashboard")}
            aria-label="Back to dashboard"
            sx={{
              color: "#E9D5FF",
              border: "1px solid rgba(168,85,247,0.35)",
              bgcolor: "rgba(255,255,255,0.04)",
              "&:hover": { bgcolor: "rgba(168,85,247,0.16)" },
            }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>

          <Stack spacing={0.25}>
            <Typography
              sx={{
                color: "#C4B5FD",
                fontWeight: 800,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontSize: { xs: 10.5, sm: 12 },
              }}
            >
              {mission?.category ?? "Active Missions"}
            </Typography>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 900, fontSize: { xs: 24, sm: 30 } }}>
              {mission?.name ?? (loading ? "Loading…" : "Mission not found")}
            </Typography>
          </Stack>

          {group && (
            <Chip
              icon={<GroupsRoundedIcon sx={{ fontSize: 17, color: "#E9D5FF !important" }} />}
              label={`Group ${group.name}`}
              title="Everyone in your group works on this same sheet"
              sx={{
                ml: "auto !important",
                bgcolor: "rgba(168,85,247,0.18)",
                color: "#E9D5FF",
                border: "1px solid rgba(168,85,247,0.4)",
                fontWeight: 800,
                fontSize: 12.5,
              }}
            />
          )}
        </Stack>

        {loading && (
          <Stack alignItems="center" sx={{ py: 10 }}>
            <CircularProgress sx={{ color: "#C4B5FD" }} />
          </Stack>
        )}

        {!loading && error && <Typography sx={{ color: "#FCA5A5" }}>Failed to load mission: {error}</Typography>}

        {!loading && !mission && !error && (
          <Typography sx={{ color: "rgba(226,232,240,0.7)" }}>No mission selected. Go back and pick one.</Typography>
        )}

        {!loading && mission && group && (
          <SheetAuthorsProvider groupId={group.id}>
            <Box
              sx={{
                borderRadius: 3,
                p: { xs: 2.75, sm: 3.5 },
                border: "1px solid rgba(168,85,247,0.24)",
                background: "linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.015) 100%)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2.5,
                    display: "grid",
                    placeItems: "center",
                    color: "#E9D5FF",
                    background: "linear-gradient(160deg, rgba(124,58,237,0.45), rgba(56,189,248,0.22))",
                    border: "1px solid rgba(196,181,253,0.35)",
                    flexShrink: 0,
                  }}
                >
                  {MissionIcon && <MissionIcon sx={{ fontSize: 30 }} />}
                </Box>

                <Stack spacing={1}>
                  <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 18, sm: 20 } }}>
                    Mission objective
                  </Typography>
                  <Typography sx={{ color: "rgba(226,232,240,0.75)", fontSize: 14.5, lineHeight: 1.65 }}>
                    {mission.objective}
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <MissionSpecPanel
              groupId={group.id}
              platformType={mission.platform_type}
              orbitType={mission.orbit_type}
              platformDetail={mission.platform_detail}
              orbitDetail={mission.orbit_detail}
            />

            {categories.length === 0 ? (
              <Chip
                label="Mission briefing in progress"
                size="small"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "rgba(250,204,21,0.12)",
                  color: "#FDE68A",
                  border: "1px solid rgba(250,204,21,0.4)",
                  fontWeight: 800,
                  fontSize: 11,
                }}
              />
            ) : (
              <>
                <Box
                  sx={{
                    borderRadius: 3,
                    p: { xs: 2.75, sm: 3.5 },
                    border: "1px solid rgba(168,85,247,0.24)",
                    background: "linear-gradient(160deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.015) 100%)",
                    backdropFilter: "blur(14px)",
                  }}
                >
                  <Stack spacing={1.5}>
                    <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>
                      Needed payload
                    </Typography>
                    <Stack spacing={1}>
                      {categories.map((category) => (
                        <Stack key={category.id} direction="row" spacing={1.25} alignItems="center">
                          <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18, color: "#A78BFA" }} />
                          <Typography sx={{ color: "rgba(226,232,240,0.85)", fontSize: 14 }}>{category.label}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>

                {categories.map((category) => (
                  <Stack key={category.id} spacing={2.5}>
                    <PayloadShowcase category={category} />
                    <Box
                      sx={{
                        borderRadius: 3,
                        p: { xs: 2, sm: 2.75 },
                        border: "1px solid rgba(168,85,247,0.24)",
                        background: "linear-gradient(160deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.012) 100%)",
                        backdropFilter: "blur(14px)",
                      }}
                    >
                      <TradeOffTable groupId={group.id} category={category} />
                    </Box>
                  </Stack>
                ))}
              </>
            )}

            <LaunchChoicePanel groupId={group.id} missionId={mission.id} />

            <Box
              sx={{
                borderRadius: 3,
                p: { xs: 2, sm: 2.75 },
                border: "1px solid rgba(168,85,247,0.24)",
                background: "linear-gradient(160deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.012) 100%)",
                backdropFilter: "blur(14px)",
              }}
            >
              <RiskMatrix groupId={group.id} />
            </Box>

            <Box
              sx={{
                borderRadius: 3,
                p: { xs: 2.75, sm: 3.5 },
                border: "1px solid rgba(168,85,247,0.35)",
                background: "linear-gradient(160deg, rgba(124,58,237,0.16) 0%, rgba(255,255,255,0.02) 100%)",
                backdropFilter: "blur(14px)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={0.5}>
                  <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: 16, sm: 17.5 } }}>
                    Done planning?
                  </Typography>
                  <Typography sx={{ color: "rgba(226,232,240,0.7)", fontSize: 13.5, lineHeight: 1.6 }}>
                    Submit your group's mission plan once everyone agrees. After that, nobody in your group can change it.
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  endIcon={<SendRoundedIcon />}
                  onClick={() => {
                    setSubmitError(null);
                    setConfirmOpen(true);
                  }}
                  sx={{
                    flexShrink: 0,
                    px: 3,
                    py: 1.1,
                    bgcolor: "#7C3AED",
                    "&:hover": { bgcolor: "#6D28D9" },
                    textTransform: "none",
                    fontWeight: 800,
                    borderRadius: 2,
                  }}
                >
                  Submit mission plan
                </Button>
              </Stack>
            </Box>
          </SheetAuthorsProvider>
        )}
      </Stack>

      <Dialog
        open={confirmOpen}
        onClose={() => (!submitting ? setConfirmOpen(false) : undefined)}
        PaperProps={{
          sx: {
            bgcolor: "#150d1c",
            border: "1px solid rgba(168,85,247,0.35)",
            borderRadius: 3,
            minWidth: { xs: "auto", sm: 440 },
          },
        }}
      >
        <DialogTitle sx={{ color: "#FFFFFF", fontWeight: 900 }}>Submit your mission plan?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <DialogContentText sx={{ color: "rgba(226,232,240,0.8)", fontSize: 14, lineHeight: 1.6 }}>
              Are you sure you want to submit your group's mission plan? There is no way to change it afterwards, for you or
              anyone else in your group.
            </DialogContentText>
            {submitError && (
              <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={submitting}
            sx={{ textTransform: "none", fontWeight: 700, color: "rgba(226,232,240,0.75)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmSubmit}
            disabled={submitting}
            variant="contained"
            sx={{ bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" }, textTransform: "none", fontWeight: 800, borderRadius: 2 }}
          >
            {submitting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
